/**
 * NBA store (PROD-588).
 *
 * Holds the live NBA candidate set per patient and is the single source
 * of truth for ClinicalDesk's NBA section. Seeded at module load from the
 * synthetic-patients fixtures (PROD-579) so every patient's three
 * canonical NBA candidates are addressable by id without re-deriving.
 *
 * Public surface:
 *   - `candidatesByPatient: Record<string, NbaCardCandidate[]>` - map of
 *     patient id to that patient's NBA candidates in the canonical order
 *     captured by the fixture.
 *   - `applyOverride(patientId, nbaId, payload)` - finds the candidate,
 *     calls the pure `applyOverrideToNba` from `services/learning-loop`
 *     (PROD-572), and replaces the candidate in the store with the
 *     post-decay record. The mutation is in-place from the consumer's
 *     point of view: the candidate count for the patient never changes
 *     (HC8 no-over-suppression).
 *
 * HC8 invariant: `applyOverride` MUST NOT remove or null any candidate.
 * The candidate count for `patientId` is identical before and after the
 * call. The integration test in `tests/clinical-desk-nba-flow.test.tsx`
 * exercises the full Open Modal -> Fill -> Submit cycle and asserts the
 * count is unchanged.
 *
 * HC9 telemetry-real: this store does NOT emit telemetry directly; the
 * underlying `applyOverrideToNba` emits `nba_confidence_adjusted` and the
 * `useOverrideStore.submitOverride` (already wired via the OverrideModal)
 * emits `override_event`. Together they cover the integration test
 * sink-assertion requirement.
 *
 * HC5: zero em / en dashes anywhere.
 */
import { create } from 'zustand';
import {
  applyOverrideToNba,
  type OverrideAdjustmentPayload,
} from '../services/learning-loop';
import {
  priorityForConfidence,
  type NbaCandidate,
} from '../types/nba';
import type { NbaCardCandidate } from '../components/NbaCard';
import {
  SYNTHETIC_PATIENTS,
  type PatientFixture,
} from '../data/synthetic-patients';
import type { Citation, Nba } from '../data/synthetic-patients/types';

/**
 * Convert a synthetic-patient fixture NBA into the card-facing candidate
 * shape the store + `<NbaCard>` operate on. Pure helper; deterministic.
 */
export function fixtureNbaToCardCandidate(
  patient: PatientFixture,
  nba: Nba,
  pathwayId: string,
  stepId: string,
): NbaCardCandidate {
  const base: NbaCandidate = {
    id: nba.id,
    pathwayId,
    stepId,
    tenantId: patient.tenant,
    title: nba.title,
    confidence: nba.confidence,
    priority: priorityForConfidence(nba.confidence),
    state: 'active',
    overrideCount: 0,
    lastOverrideAt: null,
  };
  const citations: ReadonlyArray<Citation> = nba.cited_sources;
  return {
    ...base,
    rationale: nba.rationale,
    citations,
    // Forward the optional UAE-overlay validation tag so the NBA card
    // renders the `<ValidationTagBadge>` (Wave 5 Phase A). When the
    // fixture does not carry a tag, the card renders no badge.
    ...(nba.validationTag !== undefined
      ? { validationTag: nba.validationTag }
      : {}),
  };
}

/**
 * Build the initial `candidatesByPatient` map from the canonical patient
 * fixtures. Pathway + step ids follow the `tlp{n}-...` + `step-1`
 * convention used by the existing learning-loop fixtures.
 */
function seedCandidatesByPatient(): Record<string, NbaCardCandidate[]> {
  const map: Record<string, NbaCardCandidate[]> = {};
  for (const patient of SYNTHETIC_PATIENTS) {
    const pathwayId = pathwayVersionIdForPathway(patient.pathway);
    const stepId = 'step-1';
    map[patient.id] = patient.nbas.map((nba) =>
      fixtureNbaToCardCandidate(patient, nba, pathwayId, stepId),
    );
  }
  return map;
}

/**
 * Derive the canonical pathway version id for a patient's pathway slug.
 * The version id remains the legacy `tlp{n}-...-v1` string per the Brand
 * Patch (vault: 06-decisions/care-pathway-studio/2026-05-06-brand-and-
 * naming-cleanup-patch.md) so telemetry wire format stays stable.
 */
function pathwayVersionIdForPathway(
  pathway: PatientFixture['pathway'],
): string {
  switch (pathway) {
    case 'cardiometabolic-pathway':
      return 'tlp1-glp1-v1';
    case 'hrt-pathway':
      return 'tlp2-hrt-v1';
    case 'metabolic-syndrome-pathway':
      return 'tlp3-metsyn-v1';
    case 'cvd-prevention-pathway':
      return 'tlp4-cvd-v1';
  }
}

export interface NbaStoreState {
  /** patient id -> ordered NBA candidates for that patient. */
  candidatesByPatient: Record<string, NbaCardCandidate[]>;
  /**
   * Apply an override to a candidate. Replaces the candidate in-place; the
   * candidate count for the patient is unchanged (HC8). Returns the
   * post-decay candidate or `null` when no candidate matches the
   * `(patientId, nbaId)` pair (defensive; the caller should not see this
   * in a normal flow).
   */
  applyOverride: (
    patientId: string,
    nbaId: string,
    payload: OverrideAdjustmentPayload,
  ) => NbaCardCandidate | null;
  /** Reset the store to the seeded fixture state. Tests + Reset Demo. */
  reset: () => void;
}

export const useNbaStore = create<NbaStoreState>((set, get) => ({
  candidatesByPatient: seedCandidatesByPatient(),
  applyOverride: (patientId, nbaId, payload) => {
    const list = get().candidatesByPatient[patientId];
    if (!list) {
      console.warn(
        `[nba.store] applyOverride: no candidates for patient "${patientId}"`,
      );
      return null;
    }
    const idx = list.findIndex((c) => c.id === nbaId);
    if (idx === -1) {
      console.warn(
        `[nba.store] applyOverride: no candidate with nba_id "${nbaId}" for patient "${patientId}"`,
      );
      return null;
    }
    const current = list[idx];
    // Delegate to the pure learning-loop function; it emits the
    // `nba_confidence_adjusted` telemetry event per HC9.
    const reduced = applyOverrideToNba(payload, current);
    // Preserve the card-only fields (rationale + citations) which the
    // learning-loop record does not carry. Also forward the optional
    // UAE-overlay `validationTag` so the badge keeps rendering after an
    // override (Wave 5 Phase B fix). An override changes the clinician's
    // chosen action, not the underlying evidence base, so the tag's
    // epistemic status is unchanged and should remain visible.
    const next: NbaCardCandidate = {
      ...reduced,
      rationale: current.rationale,
      citations: current.citations,
      ...(current.validationTag !== undefined
        ? { validationTag: current.validationTag }
        : {}),
    };
    const replaced = [...list];
    replaced[idx] = next;
    set({
      candidatesByPatient: {
        ...get().candidatesByPatient,
        [patientId]: replaced,
      },
    });
    return next;
  },
  reset: () => {
    set({ candidatesByPatient: seedCandidatesByPatient() });
  },
}));

/**
 * Convenience selector: return the candidate list for the given patient
 * id, or an empty array if the patient has no candidates yet.
 */
export function selectCandidatesForPatient(
  state: NbaStoreState,
  patientId: string | null,
): NbaCardCandidate[] {
  if (!patientId) return [];
  return state.candidatesByPatient[patientId] ?? [];
}
